import React, { useState } from 'react'
import {
  Grid, TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel,
  Checkbox, Button, Box, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, CircularProgress, Chip
} from '@material-ui/core'
import { previewAudience } from './campaignsApi'

const STATUS_OPTIONS = ['', 'Won', 'Lost', 'Open']

/**
 * Builds an audience filter and previews the resolved recipient count + sample
 * against the locally-synced Aspire data (fast, no live Aspire calls).
 */
export default function AudienceBuilder ({ url, filter, onChange }) {
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const set = (key, value) => onChange({ ...filter, [key]: value })

  const runPreview = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await previewAudience(url, filter)
      setPreview(res)
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>Audience Filter</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Opportunity Status</InputLabel>
            <Select
              label="Opportunity Status"
              value={filter.opportunityStatusName || ''}
              onChange={(e) => set('opportunityStatusName', e.target.value)}
            >
              {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s || 'Any'}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="Division" variant="outlined" size="small" fullWidth
            value={filter.divisionName || ''} onChange={(e) => set('divisionName', e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="Company name contains" variant="outlined" size="small" fullWidth
            value={filter.companyName || ''} onChange={(e) => set('companyName', e.target.value)} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="City" variant="outlined" size="small" fullWidth
            value={filter.city || ''} onChange={(e) => set('city', e.target.value)} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="State" variant="outlined" size="small" fullWidth
            value={filter.stateProvinceCode || ''} onChange={(e) => set('stateProvinceCode', e.target.value)} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Zip" variant="outlined" size="small" fullWidth
            value={filter.zipCode || ''} onChange={(e) => set('zipCode', e.target.value)} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField label="Contact type" variant="outlined" size="small" fullWidth
            value={filter.contactTypeName || ''} onChange={(e) => set('contactTypeName', e.target.value)} />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox checked={filter.requireEmailFlags !== false}
              onChange={(e) => set('requireEmailFlags', e.target.checked)} />}
            label="Only contacts flagged to receive invoices/notifications"
          />
          <FormControlLabel
            control={<Checkbox checked={filter.onlyActive !== false}
              onChange={(e) => set('onlyActive', e.target.checked)} />}
            label="Active contacts only"
          />
        </Grid>
      </Grid>

      <Box mt={2}>
        <Button variant="contained" color="primary" onClick={runPreview} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Preview recipients'}
        </Button>
        {preview && (
          <Chip label={`${preview.count} recipients`} color="secondary" style={{ marginLeft: 12 }} />
        )}
      </Box>

      {error && <Typography color="error" variant="body2" style={{ marginTop: 8 }}>{error}</Typography>}

      {preview && preview.sample?.length > 0 && (
        <Box mt={2}>
          <Typography variant="caption" color="textSecondary">
            Sample (first {preview.sample.length} of {preview.count})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Company</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.sample.map((r, i) => (
                <TableRow key={`${r.email}-${i}`}>
                  <TableCell>{r.firstName}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{r.companyName}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  )
}
